import { useState, useCallback, useMemo, useEffect } from "react";
import { useRouter } from "next/router";
import { Row, Col, message, Spin } from "antd";
import { Loading3QuartersOutlined } from "@ant-design/icons";
import { PaymentStatusStepAssets } from "@dreambigger/shared/src/types";
import { StepContentSection, StripePaymentStatus } from "../components";
import { StepProps } from "../pages/flows/[flowId]";
import { useSegment } from "@dreambigger/shared/src/hooks";
import { useApplication } from "../api";
import { paymentIntentStatusKey, FORWARD_PROGRESS_STATUSES } from "./payment";
import useNextStep from "../hooks/use-next-step";

const spinnerIcon = <Loading3QuartersOutlined spin />;

export default function PaymentStatus({
  flow,
  step,
  brand,
  progress,
}: StepProps) {
  const [loading, setLoading] = useState(true);
  const segment = useSegment();
  const router = useRouter();
  const assets: PaymentStatusStepAssets = step.assets;
  const applicationHelper = useApplication(flow.financialInstitution.id);
  const application = useMemo(
    () => applicationHelper.find(flow.id, "draft"),
    [applicationHelper, flow]
  );
  const nextStep = useNextStep(assets, applicationHelper, flow, step.slug);

  // Check if payment has already been submitted
  const savedStatus = useMemo(() => {
    if (!application || !assets.previousSlug) return undefined;
    const status = applicationHelper.findResponseField(
      flow.id,
      "draft",
      assets.previousSlug,
      paymentIntentStatusKey
    );
    return status ? String(status) : undefined;
  }, [application, applicationHelper, flow.id, assets.previousSlug]);

  const isPaymentAlreadySubmitted =
    savedStatus && FORWARD_PROGRESS_STATUSES.includes(savedStatus as any);

  // If payment already submitted, skip directly to next step
  useEffect(() => {
    if (isPaymentAlreadySubmitted && assets.nextSlug) {
      nextStep({ status: savedStatus });
    }
  }, [isPaymentAlreadySubmitted, assets.nextSlug, savedStatus, nextStep]);

  const handlePaymentFinish = useCallback(
    async (status: string) => {
      setLoading(false);

      // Helper to save payment status to payment step so it shows "Payment Submitted" on revisit
      const savePaymentStatus = async () => {
        if (!assets.previousSlug || !application) {
          return;
        }

        try {
          // Get existing payment step response to preserve all fields
          const existingResponse = applicationHelper.findResponseByApplicationId(
            application.id,
            assets.previousSlug
          );

          if (!existingResponse) {
            message.error(
              "Unable to save payment details. Please contact support if the issue persists."
            );
            console.error(
              `Payment status save failed: previousSlug "${assets.previousSlug}" not found in application responses`
            );
            return;
          }

          await applicationHelper.update(application.id, {
            slug: assets.previousSlug,
            type: "payment",
            fields: {
              ...(existingResponse.fields || {}),
              [paymentIntentStatusKey]: status,
            },
          });
        } catch (error) {
          console.error("Failed to save payment status:", error);
          // Don't throw - allow the flow to continue even if status save fails (extremely unlikely)
        }
      };

      // https://stripe.com/docs/payments/paymentintents/lifecycle#intent-statuses
      switch (status) {
        case "succeeded":
          segment.track({ action: "Payment", label: "Success" });
          await savePaymentStatus();
          nextStep({ status });
          break;
        case "requires_capture":
          segment.track({ action: "Payment", label: "Requires Capture" });
          await savePaymentStatus();
          nextStep({ status });
          break;
        case "processing":
          message.info("Your payment is being processed.");
          segment.track({ action: "Payment", label: "Processing" });
          await savePaymentStatus();
          nextStep({ status });
          break;
        case "requires_action":
          message.info("Your payment requires additional verification.");
          segment.track({ action: "Payment", label: "Requires Action" });
          await savePaymentStatus();
          nextStep({ status });
          break;
        case "requires_payment_method":
          message.error(
            "Payment failed. Please try again with a different method. Reloading form..."
          );
          segment.track({ action: "Payment", label: "Failed" });
          router.push({
            query: {
              ...router.query,
              stepSlug: step.assets.previousSlug,
            },
          });
          break;
        default:
          message.error(
            "Payment failed. Please try again with a different method. Reloading form..."
          );
          segment.track({ action: "Payment", label: "Error" });
          router.push({
            query: {
              ...router.query,
              stepSlug: step.assets.previousSlug,
            },
          });
          break;
      }
    },
    [
      application,
      applicationHelper,
      flow.id,
      assets.previousSlug,
      segment,
      router,
      step.assets.previousSlug,
      nextStep,
    ]
  );

  // Show spinner if application is loading OR if payment already submitted (will redirect)
  const showSpinner = !application || isPaymentAlreadySubmitted;

  return (
    <StepContentSection
      brand={brand}
      stepProgress={progress}
      title={showSpinner ? "" : assets.title ?? ""}
      description={showSpinner ? "" : assets.description ?? ""}
      previousSlug={assets.previousSlug}
      hideProgressBar={assets.hideProgressBar}
    >
      {showSpinner ? (
        <div
          className="flex items-center justify-center"
          style={{ minHeight: "30vh" }}
        >
          <Spin indicator={spinnerIcon} />
        </div>
      ) : (
        <StripePaymentStatus
          onFinish={handlePaymentFinish}
          stripeAccount={assets.stripeAccountId}
        >
          <Row>
            <Col xs={24} md={16}>
              {loading && (
                <div>
                  <Spin indicator={spinnerIcon} />
                </div>
              )}
            </Col>
          </Row>
        </StripePaymentStatus>
      )}
    </StepContentSection>
  );
}
